/**
 * Divider Component
 */

import { IBuilderComponent } from '../../base'
import type {
  ColorAdaptiveValue,
  ColorVariant,
  ComponentAlignment,
  ResponsiveValue,
  SpacingInputValue,
  TextAlignment,
  TypographyValue,
  VariableTextValue,
} from '../../types'
import {
  formatComponentAlignmentFormat,
  formatTextAlignmentFormat,
  formatColorFormat,
  formatFromVariableText,
  formatSpacingFormat,
  formatTypographyFormat,
  formatWidthFormat,
} from '../../format-utils'
import {
  deepMerge,
  parseAlignmentFormat,
  parseColorFormat,
  parseSpacingFormat,
  parseToVariableText,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import dividerTemplate from './divider.template.json'

const DIVIDER_DEFAULT_TEMPLATE = dividerTemplate as IDividerComponentSettings

export interface IDividerComponentSettings {
  // Data settings
  'divider-data-text'?: VariableTextValue

  // Design settings
  'divider-design-scheme'?: any
  'divider-design-color'?: any
  'divider-design-text-style'?: ResponsiveValue<any>
  'divider-design-title-typography'?: TypographyValue
  'divider-design-component-alignment'?: ResponsiveValue<ComponentAlignment>
  'divider-design-text-alignment'?: ResponsiveValue<TextAlignment>
  'divider-design-width'?: ResponsiveValue<string[] | number[]>
  'divider-design-line-thickness'?: ResponsiveValue<any>
  'divider-design-margin'?: ResponsiveValue<SpacingInputValue>
  'divider-design-group-color'?: any
  'divider-design-color-solid'?: ColorAdaptiveValue<ColorVariant>
  'divider-design-color-start-gradient'?: ColorAdaptiveValue<ColorVariant>
  'divider-design-color-end-gradient'?: ColorAdaptiveValue<ColorVariant>
  'divider-design-color-gradient-direction'?: ColorAdaptiveValue<ColorVariant>
  'divider-design-inset'?: any
}

export interface IDividerComponent extends IBuilderComponent {
  componentId: 'divider'
  groupId: 'visual'
  settings: IDividerComponentSettings
}

export interface AiDividerInput {
  text?: string[] // Format: ["text:OR"] or ["text:Section ", "cr:title"]
  width?: string
  alignment?: string
  margin?: string
  color?: string
  lineThickness?: string | number
  typography?: string
}

export class DividerComp {
  getTemplate(): IDividerComponentSettings {
    return DIVIDER_DEFAULT_TEMPLATE
  }

  /**
   * format divider component settings to AI divider input
   */
  format(settings: IDividerComponentSettings): AiDividerInput {
    const template = this.getTemplate()
    const result: AiDividerInput = {}

    // Text
    if (settings['divider-data-text']) {
      const text = formatFromVariableText(settings['divider-data-text'])
      if (text.length > 0 && text[0] !== 'text:') {
        result.text = text
      }
    }

    // Width
    const width = formatWidthFormat(settings['divider-design-width'])
    if (width) {
      result.width = width
    }

    // Alignment - combine component alignment and text alignment
    const compAlign = formatComponentAlignmentFormat(settings['divider-design-component-alignment'])
    const textAlign = formatTextAlignmentFormat(settings['divider-design-text-alignment'])
    const alignParts: string[] = []
    if (compAlign) {
      alignParts.push(compAlign)
    }
    if (textAlign) {
      alignParts.push(textAlign)
    }
    if (alignParts.length > 0) {
      result.alignment = alignParts.join(' ')
    }

    // Margin
    const margin = formatSpacingFormat(settings['divider-design-margin'])
    if (margin) {
      result.margin = margin
    }

    // Color
    const color = formatColorFormat({
      colorType: settings['divider-design-color'] as any,
      colorSolid: settings['divider-design-color-solid'],
      colorStartGradient: settings['divider-design-color-start-gradient'],
      colorEndGradient: settings['divider-design-color-end-gradient'],
      colorGradientDirection: settings['divider-design-color-gradient-direction'] as any,
    })
    if (color) {
      result.color = color
    }

    // Line Thickness
    if (settings['divider-design-line-thickness']) {
      const thicknessValue = settings['divider-design-line-thickness'].phone?.[0]
      if (thicknessValue !== undefined) {
        // Extract number from string like "2px" -> 2
        const numMatch = String(thicknessValue).match(/(\d+)/)
        if (numMatch) {
          result.lineThickness = parseInt(numMatch[1])
        } else {
          result.lineThickness = thicknessValue as any
        }
      }
    }

    // Typography
    if (settings['divider-design-title-typography']) {
      const typo = formatTypographyFormat({
        phone: settings['divider-design-title-typography'],
        'phone-landscape': settings['divider-design-title-typography'],
        tablet: settings['divider-design-title-typography'],
        'tablet-landscape': settings['divider-design-title-typography'],
        desktop: settings['divider-design-title-typography'],
        tv: settings['divider-design-title-typography'],
      } as any)
      if (typo) {
        result.typography = typo
      }
    }

    return result
  }

  /**
   * parse AI divider input to divider component settings
   */
  parse(input: AiDividerInput): IDividerComponentSettings {
    const result = deepMerge(DIVIDER_DEFAULT_TEMPLATE, {})

    // Data - Text
    if (input.text !== undefined) {
      result['divider-data-text'] = parseToVariableText(input.text)
    }

    // Design - Width
    if (input.width) {
      result['divider-design-width'] = parseWidthFormat(input.width)
    }

    // Design - Alignment
    if (input.alignment) {
      const alignmentParsed = parseAlignmentFormat(input.alignment)
      if (alignmentParsed.hAlign) {
        result['divider-design-component-alignment'] = alignmentParsed.hAlign
      }
      if (alignmentParsed.textAlign) {
        result['divider-design-text-alignment'] = alignmentParsed.textAlign
      }
    }

    // Design - Margin
    if (input.margin) {
      result['divider-design-margin'] = parseSpacingFormat(input.margin)
    }

    // Design - Color
    if (input.color) {
      const colorParsed = parseColorFormat(input.color)
      result['divider-design-color'] = colorParsed.colorType
      result['divider-design-color-solid'] = colorParsed.colorSolid
      result['divider-design-color-start-gradient'] = colorParsed.colorStartGradient
      result['divider-design-color-end-gradient'] = colorParsed.colorEndGradient
      result['divider-design-color-gradient-direction'] = colorParsed.colorGradientDirection as any
    }

    // Design - Line Thickness
    if (input.lineThickness !== undefined) {
      if (typeof input.lineThickness === 'string') {
        // Parse responsive format if string
        const thicknessValue = input.lineThickness.includes(':')
          ? input.lineThickness.split(':')[1]
          : input.lineThickness
        result['divider-design-line-thickness'] = {
          phone: [thicknessValue],
          'phone-landscape': [thicknessValue],
          tablet: [thicknessValue],
          'tablet-landscape': [thicknessValue],
          desktop: [thicknessValue],
          tv: [thicknessValue],
        }
      } else {
        // Single number value
        result['divider-design-line-thickness'] = {
          phone: [`${input.lineThickness}px`],
          'phone-landscape': [`${input.lineThickness}px`],
          tablet: [`${input.lineThickness}px`],
          'tablet-landscape': [`${input.lineThickness}px`],
          desktop: [`${input.lineThickness}px`],
          tv: [`${input.lineThickness}px`],
        }
      }
    }

    // Design - Typography
    if (input.typography) {
      const typo = parseTypographyFormat(input.typography)
      // Apply to title typography if it exists
      if (result['divider-design-title-typography']) {
        // For single typography value, use the first breakpoint value
        const firstBreakpoint = Object.values(typo)[0]
        if (firstBreakpoint) {
          result['divider-design-title-typography'] = firstBreakpoint
        }
      }
    }

    return result
  }
}

